
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import _ from 'lodash';
    import moment from 'moment';
    import { SweetAlertResult } from 'sweetalert2';
    import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import PatUntersuchung from '@/types/PatUntersuchung';
    import PlaeneDetailDatatable from '@/components/Impfverwaltung/Plaene/PlaeneDetailDatatable.vue';
    import PlanNeueImpfung from '@/components/Impfverwaltung/Plaene/PlanNeueImpfung.vue';
    import PlanNeueUntersuchung from '@/components/Impfverwaltung/Plaene/PlanNeueUntersuchung.vue';
    import Planimpfung from '@/types/Planimpfung';
    import Reiseplan, { PlanItem } from '@/types/Reiseplan';
    import {confirmSwal, questionSwal, warningSwal} from '@/helper/swals';
    import { ValidationRules } from '@/store/types';
    import { VForm } from '@/types/Components';
    import Patient from '@/types/Patient';
    import { CreatePlanPayload } from '@/store/patientreiseplaene';
    import { LoadReiseartenPayload } from '@/store/reisearten';
    import { LoadReisezielePayload } from '@/store/reiseziele';
    import Reiseziel from '@/types/Reiseziel';
    import {LoadPersonenPayload} from '@/store/personen';
    import Karteieintrag from '@/types/Karteieintrag';
    import api from '@/services/Api';
    import {AxiosResponse} from 'axios';

    @Component({
        components: {
            EingetragenAmVom,
            PlaeneDetailDatatable,
            PlanNeueImpfung,
            PlanNeueUntersuchung,
        },
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('reise', ['reiseziele', 'reisearten']),
            ...mapState('reise', { reiseLoaders: 'loaders' }),
            ...mapState('patient', ['dialogs', 'loaders', 'patientData', 'selected_reiseplan', 'selected_text']),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('personen', { personenLoaders: 'loaders' }),
            ...mapState('session', ['arzt']),
            ...mapGetters('authentication', ['isAllowed', 'getSystemEinstellung']),
        },
        methods: {
            ...mapActions('patient', ['createPlan', 'updatePlan', 'deletePlan', 'printPlan']),
            ...mapGetters('patient', ['getImpfstoffeReiseplan', 'getUntersuchungenReiseplan']),
            ...mapActions('reise', ['loadReisearten', 'loadReiseziele']),
            ...mapActions('personen', ['loadPersonen']),
            ...mapMutations('session', ['setSelectedArzt']),
        },
    })
    export default class PlanDetailmaske extends Vue {
        @Prop() value!: Reiseplan;
        @Prop() visible!: boolean;
        @Prop({ default: () => false }) isReadonly!: boolean;
        @Prop() valueKartei!: Karteieintrag;

        public $refs!: {
            form: VForm;
        };

        public search = '';

        public rules!: ValidationRules;
        public parseDate!: (input: string | null) => string | null;
        public formatDate!: (input: string | null) => string | null;
        public getImpfstoffeReiseplan!: () => Planimpfung[];
        public getUntersuchungenReiseplan!: () => PatUntersuchung[];
        public patientData!: Patient;
        public loadReiseziele!: (payload?: LoadReisezielePayload) => void;
        public loadReisearten!: (payload?: LoadReiseartenPayload) => void;
        public createPlan!: (payload: CreatePlanPayload) => void;
        public deletePlan!: () => void;
        public printPlan!: (payload: Reiseplan) => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public text = new Karteieintrag();
        public arzt!: number | null;

        public plan = new Reiseplan();
        public dialogNeueImpfung = false;
        public dialogNeueUntersuchung = false;
        public planimpfungen = [];
        public planuntersuchungen = [];

        public items: PlanItem<Planimpfung | PatUntersuchung>[] = [];

        public get isNew(): boolean {
            return this.plan.patplan_id === null;
        }

        public dialogWidth = 1300;

        public onResize(): void {
            const resize = (): void => {
                this.dialogWidth = Math.min(1300, window.innerWidth / (this.$vuetify.breakpoint.mobile ? 1 : 2));
            };

            resize();
            setTimeout(resize, 1000);
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (visible) {
                this.plan = new Reiseplan(this.value);
                this.plan.datum = moment(this.plan.datum).format('YYYY-MM-DD');

                const apply = <
                    T extends Planimpfung | PatUntersuchung,
                    V extends PlanItem<T>,
                    F extends (c: T) => V,
                    M extends (x: T) => T,
                >(a: F, b: M): (x: T) => V => (x: T): V => a(b(x));

                const cloneImpfung = (planimpfung: any): Planimpfung => {
                    return new Planimpfung({
                        ...planimpfung,
                        impfdatum_geplant: this.parseDate(planimpfung.impfdatum_geplant),
                    });
                };

                const cloneUntersuchung = (untersuchung: any): PatUntersuchung => {
                    return new PatUntersuchung({
                        ...untersuchung,
                        abnahmedatum: this.parseDate(untersuchung.abnahmedatum),
                    });
                };

                const planimpfungen = this.getImpfstoffeReiseplan()
                    .map(apply(PlanItem.from, cloneImpfung));
                const planuntersuchungen = this.getUntersuchungenReiseplan()
                    .map(apply(PlanItem.from, cloneUntersuchung));

                this.items = _.sortBy([
                    ...planimpfungen,
                    ...planuntersuchungen,
                ], ['datumGeplant', 'displayText']);

                if (this.isNew) {
                    this.plan.patnr = this.patientData.patnr;
                }

                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }

                this.datumErrorMessages = [];
                this.datumFormatted = this.formatDate(this.plan.datum);
                this.abreisedatumErrorMessages = [];
                this.abreisedatumFormatted = this.formatDate(this.plan.abreisedatum);
                this.text = new Karteieintrag(this.valueKartei);
                this.loadPersonen({ filter: { impfstelle_id: this.text.impfstelle_id as number } });

                if (!this.isReadonly) {
                    this.loadReiseziele();
                    this.loadReisearten();
                }
                if (this.isNew) {
                    if (this.arzt) {
                        this.plan.person_id_durchf = this.arzt;
                    }
                }
            }
        }

        public onClickSave(): void {
            this.validateAbreisedatum();

            if (this.$refs.form.validate() &&
                this.datumErrorMessages.length === 0 &&
                this.abreisedatumErrorMessages.length === 0) {
                this.createPlan({
                    data: this.plan,
                    planimpfungen: this.items
                        .filter((item) => item.isImpfung)
                        .map((item) => item.asImpfung),
                    planuntersuchungen: this.items
                        .filter((item) => item.isUntersuchung)
                        .map((item) => item.asUntersuchung),
                });
            }
        }

        public onClickNewImpfung(): void {
            this.dialogNeueImpfung = true;
        }

        public onClickNewUntersuchung(): void {
            this.dialogNeueUntersuchung = true;
        }

        public onClickAddImpfung(impfungen: Planimpfung[]): void {
            _.forEach(impfungen, (item: any) => {
                item.isDirty = true;
            });

            this.items.push(...impfungen.map(PlanItem.from));
        }

        public onClickAddUntersuchung(untersuchung: PatUntersuchung): void {
            untersuchung.patplan_id = this.plan.patplan_id;
            untersuchung.isDirty = true;
            this.items.push(PlanItem.from(untersuchung));
        }

        public onClickRemoveImpfung(impfung: Planimpfung): void {
            this.items.splice(this.items.findIndex((item) => item.data === impfung), 1);
        }

        public onClickDeclineImpfung(impfung: Planimpfung): void {
            const item = this.items
                .find((item) => item.isImpfung && item.asImpfung.patplanimpfstoff_id === impfung.patplanimpfstoff_id);

            if (item) {
                item.asImpfung.abgelehnt = 'j';
            }
        }

        public onClickAcceptImpfung(impfung: Planimpfung): void {
            const item = this.items
                .find((item) => item.isImpfung && item.asImpfung.patplanimpfstoff_id === impfung.patplanimpfstoff_id);

            if (item) {
                item.asImpfung.genehmigt = 'j';
            }
        }

        public onClickRemoveUntersuchung(untersuchung: PatUntersuchung): void {
            this.items.splice(this.items.findIndex((item) => item.data === untersuchung), 1);
        }

        public onClickCloseImpfung(): void {
            this.dialogNeueImpfung = false;
        }

        public onClickCloseUntersuchung(): void {
            this.dialogNeueUntersuchung = false;
        }

        public onClickDelete(): void {
            if (this.items.some((item) => item.isImpfung && item.asImpfung.geimpft === 'j') ||
                this.items.some((item) => item.isUntersuchung && item.asUntersuchung.befunddatum !== null)) {
                warningSwal(
                    'Hinweis',
                    'Dieser Plan kann nicht gelöscht werden, da schon eine oder mehrere Impfungen bzw.' +
                        ' Untersuchungen durchgeführt wurden!',
                    'Schließen',
                );
            } else {
                questionSwal('Löschen', 'Wollen Sie den Reiseplan wirklich löschen?', 'Löschen', 'Abbrechen')
                    .then(({ isConfirmed }: SweetAlertResult) => {
                        if (isConfirmed) {
                            this.deletePlan();
                        }
                    });
            }
        }

        public onClickPrint(): void {
            this.printPlan(this.plan);
        }

        public onClickReisezieleCheckbox(reiseziel: Reiseziel): void {
            const reisezielIndex = this.plan.reiseziele.indexOf(reiseziel.reiseziel_id as number);
            if (reisezielIndex !== -1) {
                this.plan.reiseziele.splice(reisezielIndex, 1);
            } else {
                this.plan.reiseziele.push(reiseziel.reiseziel_id as number);
            }
        }

        public isReisezielChecked(reiseziel: Reiseziel): boolean {
            return this.plan.reiseziele.indexOf(reiseziel.reiseziel_id as number) !== -1;
        }

        public datumMenu = false;
        public datumFormatted: string | null = null;
        public datumErrorMessages: string[] = [];

        @Watch('plan.datum')
        public onChangeDatum(date: string | null): void {
            this.datumFormatted = this.formatDate(this.plan.datum);

            if (date !== null) {
                this.datumErrorMessages = [];

                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.plan.datum);
                    if (valid !== true) {
                        this.datumErrorMessages.push(valid as string);
                    }
                }
            }
        }

        public abreisedatumMenu = false;
        public abreisedatumFormatted: string | null = null;
        public abreisedatumErrorMessages: string[] = [];

        @Watch('plan.abreisedatum')
        public onChangeAbreisedatum(date: string | null): void {
            this.abreisedatumFormatted = this.formatDate(this.plan.abreisedatum);

            if (date !== null) {
                this.validateAbreisedatum();
            }
        }

        public validateAbreisedatum(): void {
            this.abreisedatumErrorMessages = [];

            if (!this.plan.abreisedatum) {
                return;
            }

            for (const rule of [this.rules.validDate]) {
                const valid = rule(this.plan.abreisedatum);
                if (valid !== true) {
                    this.abreisedatumErrorMessages.push(valid as string);
                }
            }
        }

        public onClickClose() {
            var isDirty = false;

            _.forEach(this.items, (item: any) => {
                if (item.data.isDirty) {
                    isDirty = true;
                }
            });

            if (this.items.length > 0 && isDirty) {
                confirmSwal(
                    'Sind Sie sicher?',
                    'Sie haben auf dieser Seite Änderungen vorgenommen, die nicht gespeichert wurden. Wenn Sie die Seite verlassen, werden Sie diese Änderungen verlieren.',
                    'Ja',
                    'Nein')
                    .then(({isConfirmed}: SweetAlertResult) => {
                        if (isConfirmed) {
                            this.$emit('close');
                        }
                    });
            } else {
                this.$emit('close');
            }
        }
    }
